import React, { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import "../lib/firebase-client"; // side-effect: ensures Firebase app is initialized

const AuthContext = createContext(null);

/**
 * AuthProvider wraps the app and provides auth state via context.
 * The user object is shaped to match the old next-firebase-auth useUser() API:
 *   - .id (string | null)
 *   - .email (string | null)
 *   - .getIdToken() -> Promise<string>
 *   - .signOut() -> Promise<void>
 *   - .firebaseUser (the raw Firebase User object)
 */
export function AuthProvider({ children }) {
  // undefined = still initializing, null = confirmed unauthenticated
  const [authUser, setAuthUser] = useState(undefined);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setAuthUser({
          id: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          firebaseUser,
          getIdToken: () => firebaseUser.getIdToken(),
          signOut: async () => {
            // Call the middleware logout endpoint to clear cookies
            await fetch("/api/logout");
            await firebaseSignOut(getAuth());
            window.location.href = "/login";
          },
        });
      } else {
        setAuthUser(null);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={authUser}>{children}</AuthContext.Provider>
  );
}

/**
 * useUser() — drop-in replacement for next-firebase-auth's useUser().
 *
 * Returns the same shape:
 *   - During init: { id: null, email: null, ... }
 *   - Authenticated: { id: "uid", email: "...", getIdToken(), signOut() }
 *   - Unauthenticated: { id: null, email: null }
 */
export function useUser() {
  const authUser = useContext(AuthContext);

  // Still initializing — return a "loading" user object
  if (authUser === undefined) {
    return {
      id: null,
      email: null,
      emailVerified: false,
      firebaseUser: null,
      getIdToken: () => Promise.resolve(null),
      signOut: () => Promise.resolve(),
      _isInitializing: true,
    };
  }

  // Not authenticated
  if (authUser === null) {
    return {
      id: null,
      email: null,
      emailVerified: false,
      firebaseUser: null,
      getIdToken: () => Promise.resolve(null),
      signOut: () => Promise.resolve(),
      _isInitializing: false,
    };
  }

  return { ...authUser, _isInitializing: false };
}

/**
 * useAuthInitialized() — returns true once onAuthStateChanged has fired at least once.
 */
export function useAuthInitialized() {
  const authUser = useContext(AuthContext);
  return authUser !== undefined;
}
